import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FieldCard, FormField } from '@components';
import { getWAFFormConfig, SCREEN_BREAKPOINTS } from '@constants';
import { ApiEndpoint, FormType, InputType } from '@enums';
import {
  useAppDispatch,
  useErrorFormInner,
  useFetchFormList,
  useFormList,
  useFormLoadingStatus,
  useFormSubmissionStatus,
  useMediaQuery,
} from '@hooks';
import { submitFormRequest } from '@store';
import i18n from '@translations';
import type { FormFieldProps, WAFFormValues } from '@types';
import { validateValueEqual } from '@utils';

export const WAFFormConfiguration = ({ formikRef, onFormikInstance, options }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams();

  const formType = FormType.CdnWAF;

  useFetchFormList({
    formType,
    params: { application_id },
  });

  const data = useFormList({ formType, endpoint: ApiEndpoint.DEFAULT });

  const isSubmitting = useFormSubmissionStatus({ formType });
  const isLoading = useFormLoadingStatus({ formType });

  const wafFields = useMemo(() => getWAFFormConfig({ options })?.inputList, [options, i18n.language]);

  const handleSubmit = useCallback(
    (values: WAFFormValues) => {
      dispatch(
        submitFormRequest({
          formType,
          params: { application_id },
          formData: values,
          globalOptions: {
            returnResult: true,
          },
        }),
      );
    },
    [application_id, dispatch],
  );

  const isAboveSm = useMediaQuery(`(min-width: ${SCREEN_BREAKPOINTS.sm}px)`);

  const renderFormField = (field: FormFieldProps, formikValues: WAFFormValues) => {
    let isDisabled = isSubmitting;
    if (field.inactive) {
      const { field: watchField, condition } = field.inactive;
      const watchedValue = formikValues?.[watchField];
      isDisabled = condition(watchedValue) || isSubmitting;
    }
    const isSwitch = validateValueEqual(field.inputType, InputType.Switch);

    return (
      <FieldCard
        key={field.name}
        title={field.label}
        description={field.description}
        besideTitle={isSwitch && !isAboveSm}
      >
        <FormField
          key={field.name}
          fullWidth
          withoutLabel
          textInputClassName="self-center"
          name={field.name}
          inputType={field.inputType}
          loading={isLoading}
          disabled={isDisabled}
          placeholder={field.placeholder}
          {...(field.options && { options: field.options })}
        />
      </FieldCard>
    );
  };

  const WAFConfigurationInner = () => {
    useErrorFormInner({ formType, isSubmitting });
    return null;
  };

  const initialValues: WAFFormValues = useMemo(
    () => ({
      status: data?.status,
      log_sensitivity: data?.log_sensitivity,
      block_sensitivity: data?.block_sensitivity,
      block_action: data?.block_action,
      new_rule_status: data?.new_rule_status,
    }),
    [data],
  );

  return (
    <Formik<WAFFormValues>
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formik) => {
        if (onFormikInstance) onFormikInstance(formik);
        return (
          <div className="flex flex-col flex-1 gap-6 sm:gap-8">
            <WAFConfigurationInner />
            {wafFields.map((field, index) => renderFormField(field, formik.values))}
          </div>
        );
      }}
    </Formik>
  );
};
